/**
 * The API client.
 *
 * The **only** place this application talks to the network. Everything else
 * goes through TanStack Query hooks that call these functions, so retry,
 * caching and error shape are decided once.
 *
 * No secret ever appears here. The browser receives a payment *handoff* built
 * by the server - form fields plus a hash - and posts it to the gateway. The
 * merchant salt that produced that hash never leaves the backend.
 */

export const API_BASE_URL = __API_BASE_URL__;

/** RFC 7807 problem document, which the API returns for every failure. */
export interface ProblemDocument {
  type: string;
  title: string;
  status: number;
  detail: string;
  code: string;
  instance?: string;
  request_id?: string;
  errors?: { path: string; message: string }[];
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly problem: ProblemDocument | null;
  readonly requestId: string | undefined;

  constructor(message: string, status: number, code: string, problem: ProblemDocument | null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.problem = problem;
    this.requestId = problem?.request_id;
  }

  /** True for the failures a retry could plausibly fix. */
  get isRetryable(): boolean {
    return this.status === 0 || this.status === 408 || this.status === 429 || this.status >= 500;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** True when the endpoint itself does not exist yet on the backend. */
  get isUnimplemented(): boolean {
    return this.status === 404 || this.status === 501;
  }

  fieldErrors(): Record<string, string> {
    const result: Record<string, string> = {};
    for (const error of this.problem?.errors ?? []) result[error.path] = error.message;
    return result;
  }
}

/** Thrown when the request never reached the server. */
export class NetworkError extends ApiError {
  constructor(cause: unknown) {
    super(
      'Could not reach the server. Check your connection and try again.',
      0,
      'NETWORK_ERROR',
      null,
    );
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | undefined>;
  signal?: AbortSignal;
  /** Sent on money-moving requests so a retry cannot double-charge. */
  idempotencyKey?: string;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(`/api/v1${path}`, API_BASE_URL);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== '') url.searchParams.set(key, String(value));
  }
  return url.toString();
}

/**
 * Exported so the admin client can reuse it.
 *
 * Retry policy, credential handling and the RFC 7807 error shape are decided
 * once here. A second fetch wrapper for `/admin` would drift from this one, and
 * the difference would surface as an error that formats correctly on the
 * storefront and not in the admin panel.
 */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return request<T>(path, options);
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal, idempotencyKey } = options;

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      // Session cookies are httpOnly and first-party; no token is ever held in
      // JavaScript, so nothing here can be read by an injected script.
      credentials: 'include',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new NetworkError(cause);
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: unknown = null;
  if (text.length > 0) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const problem = (payload ?? null) as ProblemDocument | null;
    throw new ApiError(
      problem?.detail || problem?.title || `Request failed (${response.status})`,
      response.status,
      problem?.code ?? 'UNKNOWN',
      problem,
    );
  }

  return payload as T;
}

// ---------------------------------------------------------------------------
// Wire types - mirror the API's snake_case contracts exactly.
// ---------------------------------------------------------------------------

export interface VariantDto {
  id: string;
  sku: string;
  title: string;
  position: number;
  /** Integer paise. Never a float - the API never sends decimal money. */
  price_paise: number;
  compare_at_price_paise: number | null;
  weight_grams: number | null;
  status: 'active' | 'archived';
}

export interface ImageDto {
  id: string;
  url: string;
  alt: string | null;
  width: number | null;
  height: number | null;
  position: number;
  variant_id: string | null;
}

export interface ProductDto {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  description_html: string | null;
  status: 'draft' | 'active' | 'archived';
  hsn_code: string | null;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  variants: VariantDto[];
  images: ImageDto[];
}

export interface Pagination {
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface ProductListResponse {
  data: ProductDto[];
  pagination: Pagination;
}

export interface PaymentHandoffDto {
  method: 'POST' | 'REDIRECT';
  url: string;
  fields: Record<string, string>;
}

export interface PaymentInitiateResponse {
  data: {
    payment_id: string;
    transaction_id: string;
    amount_paise: number;
    currency: string;
    handoff: PaymentHandoffDto;
  };
}

export interface PaymentStatusDto {
  id: string;
  status: string;
  /** True only once the server has verified with the gateway directly. */
  verified: boolean;
  amount_paise: number;
  amount_captured_paise: number;
  amount_refunded_paise: number;
  currency: string;
  method: string | null;
  created_at: string;
  updated_at: string;
}

export interface OrderDraftLine {
  variant_id: string;
  quantity: number;
}

export interface CreateOrderRequest {
  email: string;
  phone?: string;
  lines: OrderDraftLine[];
  shipping_address: {
    first_name: string;
    last_name?: string;
    phone: string;
    line1: string;
    line2?: string;
    city: string;
    state_code: string;
    postal_code: string;
    country_code: string;
  };
}

export interface OrderDto {
  id: string;
  order_number: string;
  status: string;
  payment_status: string;
  fulfillment_status: string;
  grand_total_paise: number;
  currency: string;
  created_at: string;
}

export interface TrackingDto {
  status: string;
  tracking_number: string | null;
  tracking_url: string | null;
  carrier: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export interface ProductQuery {
  status?: string;
  q?: string;
  limit?: number;
  offset?: number;
  sort?: 'created_desc' | 'created_asc' | 'title_asc';
}

export const api = {
  health: (signal?: AbortSignal) => request<{ status: string }>('/health', { signal }),

  products: {
    list: (query: ProductQuery = {}, signal?: AbortSignal) =>
      request<ProductListResponse>('/products', {
        query: { status: 'active', limit: 24, ...query },
        ...(signal ? { signal } : {}),
      }),

    /** Accepts a slug or a public id - the API resolves either. */
    get: (identifier: string, signal?: AbortSignal) =>
      request<{ data: ProductDto }>(`/products/${encodeURIComponent(identifier)}`, {
        ...(signal ? { signal } : {}),
      }),
  },

  /**
   * Endpoints the storefront needs that the API does not expose yet.
   *
   * They are declared here rather than omitted so the client surface is the
   * contract both sides build against, and so the UI's error states are
   * exercised against a real 404 instead of being written blind.
   */
  orders: {
    create: (body: CreateOrderRequest, idempotencyKey: string) =>
      request<{ data: OrderDto }>('/orders', { method: 'POST', body, idempotencyKey }),

    get: (orderNumber: string, signal?: AbortSignal) =>
      request<{ data: OrderDto }>(`/orders/${encodeURIComponent(orderNumber)}`, {
        ...(signal ? { signal } : {}),
      }),

    track: (orderNumber: string, email: string, signal?: AbortSignal) =>
      request<{ data: TrackingDto }>(`/orders/${encodeURIComponent(orderNumber)}/tracking`, {
        query: { email },
        ...(signal ? { signal } : {}),
      }),

    mine: (signal?: AbortSignal) =>
      request<{ data: OrderDto[] }>('/account/orders', { ...(signal ? { signal } : {}) }),
  },

  checkout: {
    initiatePayment: (orderId: string, idempotencyKey: string) =>
      request<PaymentInitiateResponse>('/checkout/payments', {
        method: 'POST',
        body: { order_id: orderId },
        idempotencyKey,
      }),

    paymentStatus: (paymentId: string, signal?: AbortSignal) =>
      request<{ data: PaymentStatusDto }>(
        `/checkout/payments/${encodeURIComponent(paymentId)}`,
        { ...(signal ? { signal } : {}) },
      ),
  },

  account: {
    me: (signal?: AbortSignal) =>
      request<{ data: { email: string; first_name: string | null } }>('/account/me', {
        ...(signal ? { signal } : {}),
      }),
  },

  newsletter: {
    subscribe: (email: string) =>
      request<{ data: { subscribed: boolean } }>('/newsletter/subscribe', {
        method: 'POST',
        body: { email },
      }),
  },
};

/**
 * Submit a gateway handoff by building and posting a real form.
 *
 * A programmatic form POST rather than `fetch`: the gateway responds with an
 * HTML payment page, so the browser itself has to navigate. The fields come
 * from the server already signed - nothing here computes or holds a secret.
 */
export function submitPaymentHandoff(handoff: PaymentHandoffDto): void {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = handoff.url;
  form.style.display = 'none';

  for (const [name, value] of Object.entries(handoff.fields)) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.append(input);
  }

  document.body.append(form);
  form.submit();
}
